import { useEffect, useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import type { WireArticle } from '../../api/types'

// ── Dark-mode-aware gauge colors ──────────────────────────────────────────────
// Reads concrete Radix step variables directly — @theme aliases like
// --color-fg = var(--gray-12) are not resolved by getComputedStyle.

type GaugeColors = {
  fg: string
  fgSecondary: string
  track: string
  blue: string
  violet: string
  amber: string
  red: string
  jade: string
}

function resolveGaugeColors(): GaugeColors {
  // Use class check + hardcoded hex values — ECharts can't parse the
  // color(display-p3 ...) values that getComputedStyle returns on P3 displays.
  const isDark = document.documentElement.classList.contains('dark')
  return {
    fg:          isDark ? '#eeeeee' : '#111111',
    fgSecondary: isDark ? '#b4b4b4' : '#6f6f6f',
    track:       isDark ? '#2a2a2a' : '#e8e8e8',
    blue:        '#0090ff',
    violet:      '#6e56cf',
    amber:       isDark ? '#ffb224' : '#f76b15',
    red:         '#e5484d',
    jade:        '#29a383',
  }
}

function useGaugeColors(): GaugeColors {
  const [colors, setColors] = useState<GaugeColors>(resolveGaugeColors)
  useEffect(() => {
    const obs = new MutationObserver(() => setColors(resolveGaugeColors()))
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => obs.disconnect()
  }, [])
  return colors
}

// ── Gauge option helpers ──────────────────────────────────────────────────────

/** Round up to a visually clean max — ensures the needle is never pinned. */
function niceMax(n: number): number {
  if (n <= 5)   return 5
  if (n <= 10)  return 10
  if (n <= 20)  return 20
  if (n <= 50)  return 50
  if (n <= 100) return 100
  return Math.ceil(n / 50) * 50
}

function buildGaugeOption(
  value: number,
  max: number,
  label: string,
  arcColor: string,
  c: GaugeColors,
) {
  return {
    backgroundColor: 'transparent',
    animation: true,
    series: [{
      type: 'gauge' as const,
      startAngle: 200,
      endAngle:   -20,
      min: 0,
      max,
      radius: '84%',
      center: ['50%', '62%'],
      pointer: {
        show: true,
        length: '62%',
        width: 5,
        itemStyle: { color: c.fg },
      },
      progress: {
        show: true,
        width: 10,
        itemStyle: { color: arcColor },
      },
      axisLine: {
        lineStyle: {
          width: 10,
          color: [[1, c.track]],
        },
      },
      splitLine: { show: false },
      axisTick:  { show: false },
      axisLabel: {
        show: true,
        distance: 18,
        fontSize: 9,
        color: c.fgSecondary,
        formatter: (v: number) => (v === 0 || v === max ? String(v) : ''),
      },
      detail: {
        valueAnimation: true,
        formatter: '{value}',
        fontSize: 28,
        fontWeight: 'bold',
        fontFamily: 'Zodiak, serif',
        color: c.fg,
        offsetCenter: [0, '18%'],
      },
      title: {
        show: true,
        offsetCenter: [0, '60%'],
        fontSize: 11,
        fontWeight: '600',
        fontFamily: 'Inter, ui-sans-serif, sans-serif',
        color: c.fgSecondary,
      },
      data: [{ value, name: label }],
    }],
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

interface PipelineGaugesProps {
  articles: WireArticle[]
  myArticles: WireArticle[]
  actionNeededCount: number
  loading: boolean
  embedded?: boolean
}

export function PipelineGauges({ articles, myArticles, actionNeededCount, loading, embedded = false }: PipelineGaugesProps) {
  const c = useGaugeColors()

  const blockedCount = articles.filter(
    a => Boolean(a.workflow?.activeBlock)
  ).length

  // Compute nice maximums: "In Pipeline" needle sits ~50% at current load;
  // the others share the total in-pipeline count as their scale ceiling.
  const pipelineMax = useMemo(() => niceMax(Math.max(articles.length * 2, 10)),   [articles.length])
  const blockedMax  = useMemo(() => niceMax(Math.max(articles.length, 5)),         [articles.length])
  const myMax       = useMemo(() => niceMax(Math.max(articles.length, 5)),         [articles.length])
  const actionMax   = useMemo(() => niceMax(Math.max(myArticles.length, 5)),       [myArticles.length])

  const gauges = useMemo(() => [
    {
      key:   'pipeline',
      label: 'In Pipeline',
      value: articles.length,
      max:   pipelineMax,
      color: c.blue,
    },
    {
      key:   'mine',
      label: 'Assigned to You',
      value: myArticles.length,
      max:   myMax,
      color: c.violet,
    },
    {
      key:   'action',
      label: 'Your Turn',
      value: actionNeededCount,
      max:   actionMax,
      color: actionNeededCount > 0 ? c.amber : c.jade,
    },
    {
      key:   'blocked',
      label: 'Blocked',
      value: blockedCount,
      max:   blockedMax,
      color: blockedCount > 0 ? c.red : c.jade,
    },
  ], [articles.length, blockedCount, myArticles.length, actionNeededCount,
      pipelineMax, blockedMax, myMax, actionMax, c])

  if (loading) {
    return (
      <div className={`grid grid-cols-2 gap-4 lg:grid-cols-4 ${embedded ? '' : 'px-8 py-6'}`}>
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="flex h-44 items-center justify-center rounded border border-line bg-surface">
            <div className="h-28 w-28 animate-pulse rounded-full bg-fill" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={`grid grid-cols-2 gap-4 lg:grid-cols-4 ${embedded ? '' : 'px-8 py-6'}`}>
      {gauges.map(({ key, label, value, max, color }) => (
        <div key={key} className="rounded border border-line bg-surface">
          <ReactECharts
            option={buildGaugeOption(value, max, label, color, c)}
            style={{ height: 180 }}
            opts={{ renderer: 'svg' }}
          />
        </div>
      ))}
    </div>
  )
}

